// The Daily Briefing's pure helpers: the spellings the page shares with the
// facts the model copied ("$5,570", "68%", "6.8×"), the words for a pacing
// status, and the small rules the screen needs without a component.
// Everything here is unit-tested in briefing.test.ts.

import type {
  BriefingCampaign,
  BriefingCardStatus,
  BriefingInvoice,
  BriefingToday,
  CampaignOverviewPacing,
  CopilotStatus,
  DailyBriefing,
} from '@/api/types';
import { claudeModelLabel } from '@/lib/claude-models';
import { fmtDateShort, fmtDayShort, fmtHM, withEt } from '@/lib/datetime';

// ── spellings ───────────────────────────────────────────────────────────────

/** `$5,570` — whole dollars from cents, rounded half away from zero. */
export function usd(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const dollars = Math.floor((Math.abs(cents) + 50) / 100);
  return `${sign}$${dollars.toLocaleString('en-US')}`;
}

/** `$13.70` — to the cent. */
export function usdExact(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * What a briefing's sales mean: each show's own direct sales, the figures the
 * Campaigns page and Reports → Performance show (`shared::BRIEFING_BASIS`).
 * A briefing with any other basis is never rendered.
 */
export const BRIEFING_BASIS = 'direct-v2';

/** CPA in cents: attributed gross over direct tickets (Reports' formula). */
export function cpaCents(
  attributionGrossCents: number | null | undefined,
  ticketsMicros: number | null | undefined,
): number | null {
  if (attributionGrossCents == null || ticketsMicros == null || ticketsMicros <= 0) return null;
  return attributionGrossCents * 1_000_000 / ticketsMicros;
}

/** ROAS: direct value over attributed gross (Reports' formula). */
export function directRoas(
  valueMicros: number | null | undefined,
  attributionGrossCents: number | null | undefined,
): number | null {
  if (valueMicros == null || attributionGrossCents == null || attributionGrossCents <= 0) return null;
  return valueMicros / (attributionGrossCents * 10_000);
}

/** `255,281` */
export function count(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** `68%` of a ratio (1.0 = 100 %). */
export function pct(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

/** `6.80×`, two places as Reports shows a ROAS. */
export function roasX(x: number): string {
  return `${x.toFixed(2)}×`;
}

/** Micros of a conversion count → whole tickets. */
export function ticketsOf(micros: number): number {
  return Math.round(micros / 1_000_000);
}

/** Micros of revenue → cents. */
export function centsOf(micros: number): number {
  return Math.round(micros / 10_000);
}

/** "Sep 17" from a calendar day (`2026-09-17`), never shifted. */
export function dayShort(iso: string): string {
  return fmtDayShort(iso);
}

/**
 * "Sep 17" for an instant, on its New York day. `dayShort` reads an instant's
 * UTC date, so a briefing written at 9 PM ET read as the next day's
 * (2026-10-19).
 */
export function instantDayShort(iso: string): string {
  return fmtDateShort(iso);
}

/** "06:02 ET" — when the briefing was written. */
export function writtenAt(iso: string): string {
  return withEt(fmtHM(iso));
}

// ── words for what the numbers say ──────────────────────────────────────────

/** The pacing status in a client's words, with the ratio when there is one. */
export function paceLabel(p: CampaignOverviewPacing): string {
  const r = p.ratio ?? null;
  switch (p.status) {
    case 'behind': return r == null ? 'behind plan' : `behind plan · ${pct(r)}`;
    case 'on_pace': return r == null ? 'on pace' : `on pace · ${pct(r)}`;
    case 'ahead': return r == null ? 'ahead of plan' : `ahead of plan · ${pct(r)}`;
    case 'paused': return r == null ? 'paused' : `paused · ${pct(r)} of plan`;
    // A finished campaign's figure is final once reporting covers its last day;
    // before that the server sends no ratio, and the spend is pending.
    case 'ended': return r == null ? 'ended · spend pending' : `ended · ${pct(r)} of plan`;
    case 'not_started': return 'not started yet';
    case 'not_launched': return 'not published yet';
    default: return 'no delivery reported yet';
  }
}

/**
 * What a campaign is doing, in one or two words, from the derived state. A
 * campaign whose flights are over says so, whatever is stored: until
 * 2026-10-19 a finished one read "scheduled" or "paused". An archived one
 * says its stop is on its way until a check after the archive sees it, and
 * that it is not confirmed when the provider still delivers (2026-10-21;
 * until then Archive read "ended" at once).
 */
export function campaignStateWord(c: BriefingCampaign): string {
  const op = c.operational;
  const finished = op?.phase === 'finished';
  const ended = op?.last_flight_end ? `flight ended ${dayShort(op.last_flight_end)}` : 'flight ended';
  if (op?.requested_state === 'ended') {
    if (op.state === 'pausing') return 'stopping delivery';
    if (op.state === 'needs_attention') return 'stop not confirmed · still delivering';
  }
  switch (op?.state) {
    case 'live': return 'live';
    case 'paused': return 'paused';
    case 'paused_by_system': return 'paused by SAM';
    case 'pausing': return 'pausing';
    case 'scheduled':
      return op.phase === 'between_flights' && op.next_flight_date
        ? `between flights · next ${dayShort(op.next_flight_date)}`
        : 'scheduled';
    case 'starting': return 'starting';
    case 'partially_active': return 'partly live';
    case 'needs_attention': return finished ? `${ended} · still delivering` : 'needs attention';
    case 'unverified':
      if (finished) return `${ended} · stop not confirmed`;
      return op.observed_at ? `as last seen ${instantDayShort(op.observed_at)}` : 'not yet verified';
    // The day only when the flights are over: an archived campaign can stop before
    // its last flight, and "ended Oct 31" on Oct 21 would be a date not yet reached.
    case 'ended': return finished && op.last_flight_end ? `ended ${dayShort(op.last_flight_end)}` : 'ended';
    case 'armed': return 'armed to launch';
    default:
      return c.status === 'live' ? 'live' : c.status === 'paused' ? 'paused' : c.status === 'ended' ? 'ended' : 'not published';
  }
}

/**
 * Whether a campaign is delivering now, by its derived state: the card's tone
 * reads this, never the stored `live`, which a finished campaign keeps.
 */
export function campaignIsDelivering(c: BriefingCampaign): boolean {
  const state = c.operational?.state;
  if (state) return state === 'live' || state === 'partially_active' || state === 'starting';
  return c.status === 'live';
}

export type InvoiceTone = 'needs' | 'watch' | 'healthy' | 'quiet';

/** The Status column of the invoices table, and its tone. */
export function invoiceStatus(inv: BriefingInvoice): { label: string; tone: InvoiceTone } {
  switch (inv.state) {
    case 'pending_review': return { label: 'Awaiting approval', tone: 'needs' };
    case 'approved': return { label: 'Approved · creating', tone: 'quiet' };
    case 'xero_created': case 'pdf_stored': return { label: 'Created · not sent', tone: 'watch' };
    case 'failed': return { label: 'Failed · resume', tone: 'needs' };
    case 'emailed': case 'sent':
      if (inv.days_past_due != null) return { label: `Past due · ${inv.days_past_due} d`, tone: 'needs' };
      if ((inv.amount_due_cents ?? 0) > 0) return { label: 'Sent · unpaid', tone: 'quiet' };
      return { label: 'Sent', tone: 'quiet' };
    default: return { label: inv.state.replace(/_/g, ' '), tone: 'quiet' };
  }
}

/** `2026-W37` → `W37`; anything else unchanged. */
export function weekOf(period: string): string {
  const m = /^\d{4}-(W\d{2})$/.exec(period);
  return m ? m[1]! : period;
}

/** The status pill's CSS tone for a card or a needs-human row. */
export function statusTone(status: BriefingCardStatus): 'needs' | 'watch' | 'healthy' {
  return status === 'attention' ? 'needs' : status === 'watch' ? 'watch' : 'healthy';
}

/** The chips of the Today strip: a label and its value, only what exists. */
export function todayChips(today: BriefingToday): Array<{ label: string; value: string }> {
  const chips: Array<{ label: string; value: string }> = [];
  chips.push({
    label: 'Launches today',
    value: today.launches.length === 0 ? 'none' : today.launches.map((l) => `${l.production_name} · ${l.campaign_name}`).join(' · '),
  });
  chips.push({
    label: 'Ends tonight',
    value: today.endings.length === 0 ? 'none' : today.endings.map((l) => `${l.production_name} · ${l.campaign_name}`).join(' · '),
  });
  if (today.performances > 0) {
    chips.push({ label: 'Performances', value: `${today.performances} today` });
  }
  if (today.invoices_due > 0) {
    chips.push({ label: 'Billing', value: `${today.invoices_due} invoice${today.invoices_due === 1 ? '' : 's'} due this week` });
  }
  return chips;
}

/** Whether the Today strip has anything but "none" to say. */
export function todayIsQuiet(today: BriefingToday): boolean {
  return today.launches.length === 0 && today.endings.length === 0 && today.performances === 0 && today.invoices_due === 0;
}

/**
 * The model that wrote the briefing, for the AI-footer tooltip — provenance,
 * never a persona. A Claude id reads as its catalog label ("Claude Sonnet
 * 5.5", `@/lib/claude-models`); the DeepSeek and Gemma names are kept for
 * briefings stored before SMART's LLM moved to the Claude API (2026-11-01).
 * A facts-only briefing (`deterministic-fallback`) names no model.
 */
export function modelDisplayName(model: string): string | null {
  const m = model.toLowerCase();
  if (m === 'deterministic-fallback') return null;
  const claude = claudeModelLabel(m);
  if (claude) return claude;
  if (m.startsWith('deepseek/')) return 'DeepSeek (via OpenRouter)';
  if (m.includes('gemma')) return 'Gemma';
  if (m.includes('/')) return `${model.split('/')[0]} (via OpenRouter)`;
  return model;
}

/** The subject line the briefing was sent under, for the footer. */
export function deliveredLabel(b: DailyBriefing): string | null {
  return b.delivered.length > 0 ? `Sent · ${b.delivered.join(' · ')}` : null;
}

/**
 * How the run the reader started ended, once it has: the newest finished run
 * after the click (`before` is the newest one the click saw), read only once
 * the status has stopped writing. Comparing run instants with each other,
 * never with this browser's clock, keeps a skewed clock out of it.
 */
export function endedRun(
  status: Pick<CopilotStatus, 'generating' | 'last_generation'> | undefined,
  before: string | null | undefined,
): CopilotStatus['last_generation'] {
  if (before === undefined || !status || status.generating === true) return null;
  const last = status.last_generation ?? null;
  return last != null && last.finished_at !== before ? last : null;
}
